// src/pages/blogPost.jsx - Vollständig SEO-optimierte Version
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Container from '../components/container';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faCalendarAlt, faClock } from '@fortawesome/free-solid-svg-icons';
import BlogService from '../services/blogService';
import { Helmet } from 'react-helmet-async';

const BlogPost = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation('blog');

  const getCurrentLanguage = () => {
    const fullLang = i18n.language || localStorage.getItem('i18nextLng') || 'de_DE';
    const simpleLang = fullLang.split('_')[0];
    return { fullLang, simpleLang };
  };

  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(i18n.language || navigator.language || 'de_DE', {
        year: 'numeric', month: 'long', day: 'numeric'
      });
    } catch (e) {
      console.error('Error formatting date:', e);
      return dateString;
    }
  };

  const getTranslatedContent = (post) => {
    if (!post) return null;
    const { fullLang, simpleLang } = getCurrentLanguage();
    if (post.translations?.[fullLang]) return post.translations[fullLang];
    if (post.translations?.[simpleLang]) return post.translations[simpleLang];
    return post;
  };

  const cleanContent = (html) => {
    if (!html) return '';
    return html
      .replace(/src="\.\.\/images\/blog\//g, 'src="/images/blog/')
      .replace(/data-[^=]+="[^"]*"/g, '')
      .replace(/class=""/g, '');
  };

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const data = await BlogService.getPostBySlug(slug);
        setPost(data);
        if (data.categories?.length > 0) {
          const related = await BlogService.getPostsByCategory(data.categories[0]);
          setRelatedPosts(related.filter(p => p.slug !== slug).slice(0, 3));
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching blog post:', err);
        setError(err.message);
        setLoading(false);
      }
    };
    if (slug) {
      fetchPost();
      window.scrollTo(0, 0);
    }
  }, [slug, i18n.language]);

  if (loading) return <Container><div className="flex justify-center items-center h-64"><div className="text-primary">{t('loading')}</div></div></Container>;
  if (error || !post) return <Container><div className="flex justify-center items-center h-64"><div className="text-red-500">{error || t('post-not-found')}</div></div></Container>;

  const translated = getTranslatedContent(post);
  const isJsxContent = typeof translated.content === 'function';

  return (
    <Container>
      <Helmet>
        <title>{`${translated.title} | Blog – PathConnect`}</title>
        <meta name="description" content={translated.excerpt} />
        <link rel="canonical" href={window.location.href} />
        <meta property="og:title" content={translated.title} />
        <meta property="og:description" content={translated.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={window.location.href} />
        {post.thumbnailUrl && <meta property="og:image" content={post.thumbnailUrl} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={translated.title} />
        <meta name="twitter:description" content={translated.excerpt} />
        {post.thumbnailUrl && <meta name="twitter:image" content={post.thumbnailUrl} />}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: translated.title,
          image: post.thumbnailUrl,
          datePublished: post.date,
          dateModified: post.updated || post.date,
          author: { "@type": "Organization", name: "PathConnect" },
          publisher: {
            "@type": "Organization",
            name: "PathConnect",
            logo: { "@type": "ImageObject", url: "https://pathconnect.eu/logo.png" }
          },
          description: translated.excerpt,
          url: window.location.href
        })}</script>
      </Helmet>

      <div className="w-full px-4 sm:px-0 max-w-4xl mx-auto py-4 sm:py-8">
        <div className="mb-4 sm:mb-6">
          <Link to="/blog" className="inline-flex items-center text-primary hover:text-button-dark">
            <FontAwesomeIcon icon={faChevronLeft} className="mr-2" />{t('back-to-blog')}
          </Link>
        </div>

        <article className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
          <header className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary mb-4 font-heading break-words">
              {translated.title}
            </h1>
            <div className="flex flex-wrap items-center text-sm text-gray-500 dark:text-gray-400 mb-4">
              <div className="flex items-center mr-4 mb-2">
                <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
                {translated.formattedDate || formatDate(post.date)}
              </div>
              {post.readTime && (
                <div className="flex items-center mb-2">
                  <FontAwesomeIcon icon={faClock} className="mr-2" />
                  {post.readTime} min {t('read-time')}
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {post.categories.map(category => (
                <Link key={category} to={`/blog?category=${category}`} className="text-xs px-3 py-1 bg-gray-100 dark:bg-gray-700 text-primary rounded-full">
                  {category}
                </Link>
              ))}
            </div>
          </header>

          {post.thumbnailUrl && post.showInPostHeader !== false && !isJsxContent && (
            <div className={`mx-auto px-4 sm:px-6 mt-6 overflow-hidden ${post.thumbnailSize === 'small' ? 'max-w-md' : 'w-full'}`}>
              <img src={post.thumbnailUrl} alt={translated.title} className="w-full h-auto object-cover rounded-lg border-4 border-primary" loading="lazy" />
            </div>
          )}

          <div className="p-4 sm:p-6">
            {isJsxContent ? translated.content() : (
              <div className="blog-content prose prose-lg max-w-full dark:prose-invert" dangerouslySetInnerHTML={{ __html: cleanContent(translated.content) }} />
            )}
          </div>

          {relatedPosts.length > 0 && (
            <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-6 sm:pb-8 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4 font-heading">
                {t('related-posts')}
              </h3>
              <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 md:grid-cols-3">
                {relatedPosts.map(rp => {
                  const { fullLang, simpleLang } = getCurrentLanguage();
                  const tPost = rp.translations?.[fullLang] || rp.translations?.[simpleLang] || rp;
                  return (
                    <Link key={rp.slug} to={`/blog/${rp.slug}`} className="group block bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden shadow hover:shadow-md">
                      {rp.thumbnailUrl && (
                        <div className="h-40 overflow-hidden">
                          <img src={rp.thumbnailUrl} alt={tPost.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        </div>
                      )}
                      <div className="p-4">
                        <h4 className="font-bold text-gray-800 dark:text-white group-hover:text-primary line-clamp-2">
                          {tPost.title}
                        </h4>
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                          {tPost.excerpt}
                        </p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </article>
      </div>
    </Container>
  );
};

export default BlogPost;
